import React, { useState } from "react";
import { Tooltip, Select } from "antd";
import { FaRegTrashCan } from "react-icons/fa6";

const { Option } = Select;

const FIELDS = [
  { name: "allowed_leaves", label: "Allowed Leaves", type: "number", tooltip: "Enter the number of leaves allowed for this leave type." },
  { name: "unit", label: "Unit", type: "select", options: [["days", "Days"], ["hours", "Hours"]], tooltip: "Choose whether the leave is measured in days or hours." },
  { name: "carry_forword", label: "Carry Forward", type: "number", tooltip: "Number of unused leaves that can be carried forward to the next period." },
  { name: "prorated", label: "Prorated", type: "select", options: [["yes", "Yes"], ["no", "No"]], tooltip: "Select whether leave entitlement should be adjusted based on employment period." },
  { name: "paid_leaves", label: "Paid Leaves", type: "number", tooltip: "Enter number of leaves that will be paid by the company." },
  { name: "encashable", label: "Cashable", type: "number", tooltip: "Number of unused leaves that employees can convert into payment." },
  { name: "consecutive_allowed", label: "Consecutive Allowed", type: "number", tooltip: "Number of leaves that can be taken consecutively." },
  { name: "gender_restriction", label: "Gender Restriction", type: "select", options: [["Both", "Both"], ["Male", "Male"], ["Female", "Female"]], tooltip: "Select if this leave type is restricted to a specific gender." },
];

const LeaveCard = ({ entry, realIndex, inputFields, setInputFields, onRequestRemove }) => {
  const [expanded, setExpanded] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleChange = (name, value) => {
    const data = [...inputFields];
    data[realIndex][name] = value;
    setInputFields(data);
  };

  const getLeaveIconPath = (name) => {
    const n = (name || "").toLowerCase();
    if (n.includes("all")) return "allleaves.svg";
    if (n.includes("annual")) return "annualeaves.svg";
    if (n.includes("bereavement")) return "Bereavement.svg";
    if (n.includes("maternity")) return "maternity.svg";
    if (n.includes("paternity")) return "paternity.svg";
    if (n.includes("marriage")) return "marriage.svg";
    if (n.includes("umrah")) return "umrah.svg";
    if (n.includes("hajj")) return "hajj.svg";
    return "leaves.svg";
  };

  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          flex: 1,
          border: isFocused ? "1.5px solid #1D1D1D" : "1.5px solid #E5E7EB",
          borderRadius: "4px",
          backgroundColor: "#fff",
          overflow: "hidden",
          transition: "all 0.2s ease",
          boxShadow: isFocused ? "0 2px 12px rgba(0,0,0,0.05)" : "none"
        }}
      >
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 24px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{
            width: "35px", height: "35px", borderRadius: "8px", backgroundColor: "rgba(13, 223, 171, 0.1)",
            display: "flex", alignItems: "center", justifyContent: "center", color: "#0DDFAB"
          }}>
            <span style={{ display: 'inline-block', width: '14px', height: '14px', flexShrink: 0, backgroundColor: 'currentColor', mask: `url('/assets/images/${getLeaveIconPath(entry.leave_type_name)}') no-repeat center / contain`, WebkitMask: `url('/assets/images/${getLeaveIconPath(entry.leave_type_name)}') no-repeat center / contain`, transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', imageRendering: '-webkit-optimize-contrast' }} />
          </div>
          <span style={{ fontWeight: "600", fontSize: "16px", color: "#1D1D1D" }}>{entry.leave_type_name || "Leave Type"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            style={{
              border: "1.5px solid #E5E7EB", borderRadius: "8px", width: "32px", height: "32px",
              background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#555"
            }}
          >
            <i className={`uil-angle-${expanded ? "up" : "right"}`} style={{ fontSize: "18px" }} />
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "0 24px 8px 24px" }}>
          <div className="row">
            {FIELDS.map(field => (
              <div className="col-md-3" key={field.name}>
                {field.type === "select" ? (
                  <div className={`float-select-wrap mb-3 ${entry[field.name] ? "has-value" : ""}`}>
                    <label>
                      {field.label}
                      <Tooltip title={field.tooltip}>
                        <i className="fa fa-info-circle float-select-label-icon" />
                      </Tooltip>
                    </label>
                    <Select
                      value={entry[field.name] || undefined}
                      onChange={val => handleChange(field.name, val)}
                      placeholder=""
                      style={{ width: "100%" }}
                      suffixIcon={
                        <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                          <i className="uil-angle-down" style={{ fontSize: "16px", lineHeight: 1 }} />
                          <Tooltip title={field.tooltip}>
                            <i
                              className="fa fa-info-circle float-select-suffix-icon"
                              onMouseDown={e => e.stopPropagation()}
                            />
                          </Tooltip>
                        </span>
                      }
                    >
                      {field.options.map(([val, lbl]) => (
                        <Option key={val} value={val}>{lbl}</Option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  <div className={`float-input-wrap mb-3 ${entry[field.name] ? "has-value" : ""}`}>
                    <input
                      type="number"
                      placeholder=" "
                      value={entry[field.name] ?? ""}
                      onChange={e => handleChange(field.name, e.target.value)}
                      style={{ lineHeight: '44px' }}
                    />
                    <label>
                      {field.label}
                      <Tooltip title={field.tooltip}>
                        <i className="fa fa-info-circle float-label-helper-icon" />
                      </Tooltip>
                    </label>
                  </div>
                )}
              </div>
            ))}

            <div className="col-md-3">
              <div className={`float-select-wrap mb-3 ${entry.auto_deduction_late_in !== undefined ? "has-value" : ""}`}>
                <label>
                  Auto Deduct Late In
                  <Tooltip title="If enabled, late-in penalties will be automatically deducted from this leave type's balance.">
                    <i className="fa fa-info-circle float-select-label-icon" />
                  </Tooltip>
                </label>
                <Select
                  value={entry.auto_deduction_late_in ? "yes" : "no"}
                  onChange={val => handleChange("auto_deduction_late_in", val === "yes")}
                  placeholder=""
                  style={{ width: "100%" }}
                  suffixIcon={
                    <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                      <i className="uil-angle-down" style={{ fontSize: "16px", lineHeight: 1 }} />
                      <Tooltip title="If enabled, late-in penalties will be automatically deducted from this leave type's balance.">
                        <i
                          className="fa fa-info-circle float-select-suffix-icon"
                          onMouseDown={e => e.stopPropagation()}
                        />
                      </Tooltip>
                    </span>
                  }
                >
                  <Option value="no">Disable</Option>
                  <Option value="yes">Enable</Option>
                </Select>
              </div>
            </div>

            {(entry.auto_deduction_late_in === true || entry.auto_deduction_late_in === "yes") && (
              <div className="col-md-3">
                <div className={`float-input-wrap mb-3 ${entry.sort_order ? "has-value" : ""}`}>
                  <input
                    type="number"
                    placeholder=" "
                    value={entry.sort_order ?? 0}
                    onChange={e => handleChange("sort_order", e.target.value)}
                    style={{ lineHeight: '44px' }}
                  />
                  <label>
                    Sort Order
                    <Tooltip title="Priority for auto-deduction (Lower number = Higher priority).">
                      <i className="fa fa-info-circle float-label-helper-icon" />
                    </Tooltip>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>

      {(expanded || isHovered) && (
        <button
          type="button"
          onClick={() => onRequestRemove(realIndex)}
          style={{
            width: "30px", height: "30px",
            borderRadius: "6px",
            backgroundColor: "#F3F4F6",
            border: "1px solid #E5E7EB",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
            color: "#1D1D1D",
            transition: "all 0.2s ease",
            flexShrink: 0
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#FEE2E2"; e.currentTarget.style.color = "#EF4444"; e.currentTarget.style.borderColor = "#FCA5A5"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#F3F4F6"; e.currentTarget.style.color = "#1D1D1D"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
        >
          <FaRegTrashCan style={{ fontSize: "12px" }} />
        </button>
      )}

    </div>
  );
};

const RepeaterForm = ({ inputFields, setInputFields, filterLeaveTypeId }) => {
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null);

  const handleRequestRemove = (realIndex) => {
    setPendingRemoveIndex(realIndex);
    document.getElementById("openDeleteLeavePolicyModal").click();
  };

  const confirmRemove = () => {
    if (pendingRemoveIndex === null) return;
    setInputFields(inputFields.filter((_, i) => i !== pendingRemoveIndex));
    document.getElementById("closeDeleteLeavePolicyModal").click();
    setPendingRemoveIndex(null);
  };

  const visibleEntries = filterLeaveTypeId
    ? inputFields.filter(f => String(f.leave_type_id) === String(filterLeaveTypeId))
    : inputFields;

  return (
    <div>
      {visibleEntries.map(entry => {
        const realIndex = inputFields.findIndex(
          f => f.leave_type_id !== undefined
            ? String(f.leave_type_id) === String(entry.leave_type_id)
            : f.id === entry.id
        );
        return (
          <LeaveCard
            key={entry.leave_type_id || entry.id}
            entry={entry}
            realIndex={realIndex}
            inputFields={inputFields}
            setInputFields={setInputFields}
            onRequestRemove={handleRequestRemove}
          />
        );
      })}
      {visibleEntries.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px", color: "#aaa", fontSize: "14px" }}>
          No leave policy found for this type.
        </div>
      )}

      <div id="openDeleteLeavePolicyModal" data-bs-toggle="modal" data-bs-target="#deleteLeavePolicyModal" style={{ display: "none" }} />
      <div id="deleteLeavePolicyModal" className="modal fade" tabIndex={-1} role="dialog" aria-hidden="true" data-bs-keyboard="false">
        <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
          <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
            <div className="modal-header border-0 pb-1">
              <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Delete Leave Policy</h5>
              <button type="button" className="btn-close" id="closeDeleteLeavePolicyModal" data-bs-dismiss="modal" aria-label="Close" onClick={() => setPendingRemoveIndex(null)} />
            </div>
            <div className="modal-body pt-2 pb-2">
              <p style={{ fontSize: "14px", color: "#1D1D1D", marginBottom: "6px" }}>Are you sure you want to delete this leave policy?</p>
              <p style={{ fontSize: "13px", color: "#ff4d4f", marginBottom: 0 }}>
                <i className="uil uil-exclamation-triangle me-1" />
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer border-0 pt-1">
              <button type="button" className="btn btn-danger waves-effect waves-light" onClick={confirmRemove} style={{ borderRadius: "5px", fontSize: "13px" }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepeaterForm;
